"use client";

import * as React from "react";

/**
 * useAnchoredPanel — posiziona un pannello assoluto rispetto a un'ancora
 * (caret, selezione, trigger) **scegliendo il lato in base allo spazio a
 * schermo**, invece di crescere sempre verso il basso.
 *
 * Nasce dal pannello "Riformula con Ando": era ancorato a `caret.bottom + 8`
 * e basta, così quando la redline era lunga sfondava la finestra e allungava
 * la pagina con uno scroll infinito solo per raggiungere i bottoni. Il
 * problema non è di quel pannello: è di qualunque superficie che si apre da
 * un punto (menu contestuali, popover di suggerimento, editor inline). Da qui
 * l'hook condiviso.
 *
 * Cosa fa, in ordine:
 *  1. misura l'altezza **reale** del pannello dopo il render (`scrollHeight`,
 *     quindi l'altezza che vorrebbe avere, non quella già limitata);
 *  2. confronta lo spazio sopra e sotto l'ancora nel viewport e sceglie il
 *     lato: sotto se ci sta, altrimenti quello con più aria;
 *  3. restituisce un `maxHeight` pari allo spazio disponibile — il pannello
 *     deve scrollare al proprio interno, non spingere il documento;
 *  4. blocca il pannello dentro i bordi orizzontali del contenitore.
 *
 * Le coordinate restituite sono **relative al contenitore** (che deve essere
 * `position: relative`), quelle dell'ancora sono **di viewport** — le stesse
 * che tornano da `getBoundingClientRect()` o da `coordsAtPos()` di ProseMirror.
 *
 * Ricalcola su resize e scroll (passivi) finché il pannello è aperto: se
 * l'ancora è passata come funzione la rilegge, così il pannello segue il
 * contenuto invece di restare indietro.
 *
 * NON è un rimpiazzo di floating-ui: niente frecce, shift, portali o
 * strategie fixed. È il minimo che serve quando il pannello vive già dentro
 * un contenitore relativo e l'unica domanda è "sopra o sotto, e quanto alto".
 */

/** Rettangolo dell'ancora, in coordinate di viewport. */
export interface AnchorRect {
  readonly top: number;
  readonly bottom: number;
  readonly left: number;
}

export interface AnchoredPanelPosition {
  /** Offset dal bordo sinistro del contenitore. */
  readonly left: number;
  /** Offset dal bordo superiore del contenitore. */
  readonly top: number;
  /** Altezza massima consentita dallo spazio disponibile. */
  readonly maxHeight: number;
  /** Lato scelto — utile per orientare freccia o animazione d'entrata. */
  readonly placement: "top" | "bottom";
}

export interface UseAnchoredPanelOptions {
  /** Il pannello è visibile: a `false` l'hook non misura e non ascolta nulla. */
  readonly open: boolean;
  /** Il pannello da misurare e posizionare. */
  readonly panelRef: React.RefObject<HTMLElement | null>;
  /** Il contenitore `position: relative` a cui sono relative le coordinate. */
  readonly containerRef: React.RefObject<HTMLElement | null>;
  /**
   * L'ancora in coordinate di viewport. Come funzione viene riletta a ogni
   * ricalcolo (scroll / resize / cambio contenuto): usala quando l'ancora si
   * muove insieme al contenuto.
   */
  readonly anchor: AnchorRect | (() => AnchorRect | null) | null;
  /** Aria fra ancora e pannello, e fra pannello e bordo finestra (default 8). */
  readonly gap?: number;
  /** Sotto questa altezza il pannello non è usabile: si scrolla (default 220). */
  readonly minHeight?: number;
  /** Larghezza usata per il clamp orizzontale (default: quella misurata). */
  readonly width?: number;
  /**
   * Valori che cambiano il **contenuto** del pannello e quindi la sua altezza
   * (es. l'arrivo di un risultato). Cambiandoli si forza una rimisurazione.
   */
  readonly contentKey?: unknown;
}

export function useAnchoredPanel({
  open,
  panelRef,
  containerRef,
  anchor,
  gap = 8,
  minHeight = 220,
  width,
  contentKey,
}: UseAnchoredPanelOptions): AnchoredPanelPosition | null {
  const [position, setPosition] = React.useState<AnchoredPanelPosition | null>(null);

  // L'ancora cambia identità a ogni render quando è un oggetto letterale:
  // la teniamo in un ref per non far ripartire l'effect a vuoto.
  const anchorRef = React.useRef(anchor);
  anchorRef.current = anchor;

  const measure = React.useCallback(() => {
    const panel = panelRef.current;
    const container = containerRef.current;
    const source = anchorRef.current;
    const rect = typeof source === "function" ? source() : source;
    if (!panel || !container || !rect) return;

    const box = container.getBoundingClientRect();
    const panelWidth = width ?? panel.offsetWidth;
    const spaceBelow = window.innerHeight - rect.bottom - gap * 2;
    const spaceAbove = rect.top - gap * 2;
    const natural = panel.scrollHeight;

    const below = natural <= spaceBelow || spaceBelow >= spaceAbove;
    const maxHeight = Math.max(minHeight, below ? spaceBelow : spaceAbove);
    const top = below
      ? rect.bottom - box.top + gap
      : rect.top - box.top - Math.min(natural, maxHeight) - gap;
    const left = Math.min(
      Math.max(rect.left - box.left, gap),
      Math.max(gap, box.width - panelWidth - gap),
    );

    setPosition((prev) =>
      prev &&
      prev.top === top &&
      prev.left === left &&
      prev.maxHeight === maxHeight &&
      prev.placement === (below ? "bottom" : "top")
        ? prev
        : { left, top, maxHeight, placement: below ? "bottom" : "top" },
    );
  }, [panelRef, containerRef, gap, minHeight, width]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: `contentKey` è il segnale di "il contenuto è cambiato, rimisura"
  React.useLayoutEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }
    measure();
    const onChange = (): void => measure();
    window.addEventListener("resize", onChange, { passive: true });
    window.addEventListener("scroll", onChange, { passive: true, capture: true });
    return () => {
      window.removeEventListener("resize", onChange);
      window.removeEventListener("scroll", onChange, { capture: true });
    };
  }, [open, measure, contentKey]);

  return position;
}
